import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/stores/auth';
import PublicLayout from '@/components/layout/PublicLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import HomePage from '@/pages/public/HomePage';
import CoursesPage from '@/pages/public/CoursesPage';
import CourseDetailPage from '@/pages/public/CourseDetailPage';
import ExamsPage from '@/pages/public/SoleilExamsPage';
import ExamDetailPage from '@/pages/public/SoleilExamDetailPage';
import ExamCheckoutPage from '@/pages/public/ExamCheckoutPage';
import ExamSuccessPage from '@/pages/public/ExamSuccessPage';
import LoginPage from '@/pages/public/LoginPage';
import RegisterPage from '@/pages/public/RegisterPage';
import ContactPage from '@/pages/public/ContactPage';
import AboutPage from '@/pages/public/AboutPage';
import ForgotPasswordPage from '@/pages/public/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/public/ResetPasswordPage';
import StudentDashboard from '@/pages/student/StudentDashboard';
import StudentCoursePage from '@/pages/student/StudentCoursePage';
const LessonPlayerPage = React.lazy(() => import('@/pages/student/LessonPlayerPage'));
import ProfilePage from '@/pages/student/ProfilePage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminCoursesPage from '@/pages/admin/AdminCoursesPage';
import AdminCourseEditPage from '@/pages/admin/AdminCourseEditPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminOrdersPage from '@/pages/admin/AdminOrdersPage';
import AdminMessagesPage from '@/pages/admin/AdminMessagesPage';
import AdminExamRequestsPage from '@/pages/admin/AdminExamRequestsPage';
import AdminExamOrdersPage from '@/pages/admin/AdminExamOrdersPage';
import AdminExamProductsPage from '@/pages/admin/AdminExamProductsPage';
import AdminCouponsPage from "@/pages/admin/AdminCouponsPage";
import AdminFeatureFlagsPage from "@/pages/admin/AdminFeatureFlagsPage";
import AdminBulkEmailPage from "@/pages/admin/AdminBulkEmailPage";
import AdminInvoicesPage from "@/pages/admin/AdminInvoicesPage";
import StudentInvoicesPage from "@/pages/student/StudentInvoicesPage";
import StudentExamOrdersPage from "@/pages/student/StudentExamOrdersPage";
import AdminBulkEmailPage from "@/pages/admin/AdminBulkEmailPage";
import CheckoutPage from '@/pages/public/CheckoutPage';
import PaymentCompletePage from '@/pages/public/PaymentCompletePage';
import PaymentMockPage from '@/pages/PaymentMockPage';
import { Toaster } from 'sonner';
import ForceChangePasswordPage from '@/pages/auth/ForceChangePasswordPage';
import CheckEmailPage from '@/pages/auth/CheckEmailPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';

const queryClient = new QueryClient();

function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  // Wait for auth to finish loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Redirect if not authenticated
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.mustChangePassword) return <Navigate to="/force-change-password" />;
  
  // Redirect if trying to access admin-only route without admin role
  if (adminOnly && user?.role !== 'admin') return <Navigate to="/dashboard" />;
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - No login required */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:slug" element={<CourseDetailPage />} />
        <Route path="exams" element={<ExamsPage />} />
        <Route path="exams/:slug" element={<ExamDetailPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected Payment Routes - Login required */}
      <Route path="/check-email" element={<CheckEmailPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/force-change-password" element={<ForceChangePasswordPage />} />
      <Route path="/checkout/:courseId" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/exam-checkout/:examId" element={<ProtectedRoute><ExamCheckoutPage /></ProtectedRoute>} />
      <Route path="/exam-success" element={<ProtectedRoute><ExamSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/mock" element={<ProtectedRoute><PaymentMockPage /></ProtectedRoute>} />
      <Route path="/payment/complete" element={<ProtectedRoute><PaymentCompletePage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentCompletePage /></ProtectedRoute>} />

      {/* Student Dashboard - Login required */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<StudentDashboard />} />
        <Route path="courses/:courseId" element={<StudentCoursePage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="invoices" element={<StudentInvoicesPage />} />
        <Route path="exam-orders" element={<StudentExamOrdersPage />} />
      </Route>

      {/* Lesson Player - Full screen without sidebar */}
      <Route path="/dashboard/courses/:courseId/lessons/:lessonId" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}><LessonPlayerPage /></Suspense></ProtectedRoute>} />
      {/* Admin Panel - Admin only */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="courses" element={<AdminCoursesPage />} />
        <Route path="courses/:id" element={<AdminCourseEditPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="messages" element={<AdminMessagesPage />} />
        <Route path="exam-requests" element={<AdminExamRequestsPage />} />
        <Route path="exam-orders" element={<AdminExamOrdersPage />} />
        <Route path="exam-products" element={<AdminExamProductsPage />} />
                <Route path="coupons" element={<AdminCouponsPage />} />
                <Route path="feature-flags" element={<AdminFeatureFlagsPage />} />
                <Route path="bulk-email" element={<AdminBulkEmailPage />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="bulk-email" element={<AdminBulkEmailPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
